'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Copy, CheckCircle2, AlertCircle, Users, Shield, UserCog, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';


interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface DemoCredential {
  role: string;
  roleKey: string;
  email: string;
  password: string;
  icon: React.ElementType;
  color: string;
  redirectTo: string;
}

const demoCredentials: DemoCredential[] = [
   {
    role: 'Administrator',
    roleKey: 'admin',
    email: 'admin@peoplecore.io',
    password: 'Admin@2026!',
    icon: Shield,
    color: 'violet',
    redirectTo: '/administrator',
  },
  {
    role: 'HR Manager',
    roleKey: 'hr',
    email: 'hr.manager@peoplecore.io',
    password: 'HRMgr@2026!',
    icon: UserCog,
    color: 'blue',
    redirectTo: '/hr-manager',
  },
  {
    role: 'Employee',
    roleKey: 'employee',
    email: 'employee@peoplecore.io',
    password: 'Emp@2026!',
    icon: Users,
    color: 'emerald',
    redirectTo: '/hr-dashboard',
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; btnBg: string; btnText: string }> = {
  violet: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', btnBg: 'bg-violet-100 hover:bg-violet-200', btnText: 'text-violet-700' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', btnBg: 'bg-blue-100 hover:bg-blue-200', btnText: 'text-blue-700' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', btnBg: 'bg-emerald-100 hover:bg-emerald-200', btnText: 'text-emerald-700' },
};

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      onClick={handleCopy}
      title={`Copy ${label}`}
      className="p-1 rounded hover:bg-white/60 transition-colors"
      type="button"
    >
      {copied ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Copy size={13} className="text-gray-400 hover:text-gray-600" />}
    </button>
  );
}

export default function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wrongCredentials, setWrongCredentials] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setWrongCredentials(false);
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password }
    await new Promise(r => setTimeout(r, 1200));

    const match = demoCredentials.find(c => c.email === data.email && c.password === data.password);
    if (!match) {
      setIsLoading(false);
      setWrongCredentials(true);
      return;
    }

    toast.success(`Welcome back! Signed in as ${match.role}`, { duration: 2500 });
    await new Promise(r => setTimeout(r, 500));
    router.push(match.redirectTo);
  };

  const fillCredential = (cred: DemoCredential) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    setWrongCredentials(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-white -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-blue-400 translate-x-1/4 translate-y-1/4" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full bg-sky-300 -translate-x-1/2 -translate-y-1/2" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <AppLogo size={40} />
          <div>
            <span className="font-bold text-xl text-white tracking-tight">NGSC-HRM</span>
            <p className="text-blue-200 text-xs font-medium tracking-widest uppercase mt-0.5">HRMS Platform</p>
          </div>
        </div>

        {/* Hero copy */}
        <div className="relative space-y-6">
          <div className="space-y-3">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Your workforce,<br />
              <span className="text-sky-300">fully managed.</span>
            </h1>
            <p className="text-blue-200 text-lg leading-relaxed max-w-md">
              From hire to retire — employee lifecycle, leave management, payroll cost tracking, and workforce analytics in one platform.
            </p>
          </div>

          {/* Feature bullets */}
          <div className="space-y-3">
            {[
              'Multi-role access — Admin, HR Manager, Employee',
              'Complete employee lifecycle management',
              'Real-time workforce analytics & reporting',
              'Integrated HR Chatbot and User Manual',
            ].map((feat, i) => (
              <div key={`feat-${i + 1}`} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-sky-400/30 border border-sky-400/50 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-sky-300" />
                </div>
                <span className="text-blue-100 text-sm">{feat}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative">
          <p className="text-blue-300 text-xs">© 2026 PeopleCore HRMS. Enterprise-grade HR management.</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2.5 mb-8">
            <AppLogo size={36} />
            <span className="font-bold text-lg text-[hsl(var(--foreground))]">PeopleCore</span>
          </div>

          <div className="mb-7">
            <h2 className="text-2xl font-bold text-[hsl(var(--foreground))] tracking-tight">Sign in to your account</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">Enter your credentials or use a demo account below.</p>
          </div>

          {/* Wrong credentials error */}
          {wrongCredentials && (
            <div className="flex items-start gap-2.5 p-3 mb-5 bg-red-50 border border-red-200 rounded-lg animate-slide-up">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">Invalid credentials — use the demo accounts below to sign in.</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[hsl(var(--foreground))] mb-1.5">
                Work Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-white transition-all duration-150 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.email ? 'border-red-400 bg-red-50' : 'border-[hsl(var(--border))]'}`}
                {...register('email', {
                  required: 'Work email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle size={11} />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-[hsl(var(--foreground))]">
                  Password
                </label>
                <button type="button" className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-sm bg-white transition-all duration-150 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${errors.password ? 'border-red-400 bg-red-50' : 'border-[hsl(var(--border))]'}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle size={11} />
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2.5">
              <input
                id="remember"
                type="checkbox"
                className="w-4 h-4 rounded border-[hsl(var(--border))] text-blue-600 accent-blue-600 cursor-pointer"
                {...register('remember')}
              />
              <label htmlFor="remember" className="text-sm text-[hsl(var(--muted-foreground))] cursor-pointer">
                Keep me signed in for 30 days
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-lg transition-all duration-150 active:scale-[0.99] shadow-sm mt-1"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-7">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-[hsl(var(--border))]" />
              <span className="text-xs text-[hsl(var(--muted-foreground))] font-medium">Demo Accounts</span>
              <div className="flex-1 h-px bg-[hsl(var(--border))]" />
            </div>

            <div className="space-y-2">
              {demoCredentials.map((cred) => {
                const Icon = cred.icon;
                const colors = colorMap[cred.color];
                return (
                  <div
                    key={`cred-${cred.roleKey}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${colors.bg} ${colors.border}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${colors.bg} border ${colors.border}`}>
                      <Icon size={13} className={colors.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold ${colors.text}`}>{cred.role}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[11px] text-gray-500 font-mono truncate max-w-[140px]">{cred.email}</span>
                        <CopyButton value={cred.email} label="email" />
                        <span className="text-gray-300">·</span>
                        <span className="text-[11px] text-gray-500 font-mono">••••••••</span>
                        <CopyButton value={cred.password} label="password" />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => fillCredential(cred)}
                      className={`text-xs font-medium px-2.5 py-1 rounded-md transition-colors ${colors.btnBg} ${colors.btnText}`}
                    >
                      Use
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-center text-xs text-[hsl(var(--muted-foreground))] mt-6">
            By signing in, you agree to PeopleCore's{' '}
            <a href="#" className="text-blue-600 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}